import { Layout } from "@/components/Layout";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Shield,
  Clock,
  Sparkles,
  Mail,
  ArrowRight,
  CheckCircle2,
  MapPin,
  Wind,
  Star,
  Lock,
  BadgeCheck,
  Quote,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Rotating hero stills used by the Ken Burns / parallax component below.
// First two are real Clean Kin client homes (customer-supplied); remainder are
// licensed under the Unsplash / Pexels licence for free use.
const HERO_STILLS = [
  "https://customer-assets-agu9un31.emergentagent.net/job_eccdbd5d-5806-4fa2-9015-ef51a3d2d2b7/artifacts/9hwpzo9x_WhatsApp%20Image%202026-06-23%20at%2009.46.24.jpeg",
  "https://customer-assets-agu9un31.emergentagent.net/job_eccdbd5d-5806-4fa2-9015-ef51a3d2d2b7/artifacts/tl3psqrs_WhatsApp%20Image%202026-06-23%20at%2009.48.39%20%283%29.jpeg",
  "https://images.unsplash.com/photo-1653242370332-e332a8103763?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  "https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
  "https://images.pexels.com/photos/20285350/pexels-photo-20285350.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
  "https://images.pexels.com/photos/8709568/pexels-photo-8709568.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
];

// Client testimonial quotes — used verbatim from the customer brief for the
// motion-driven carousel further down the page.
const CLIENT_TESTIMONIALS = [
  { quote: "Highly recommend.",              author: "A. Green",  role: "Verified customer" },
  { quote: "Meticulous, thorough.",          author: "T. Fisher", role: "Verified customer" },
  { quote: "High standard.",                 author: "R. Patel",  role: "Verified customer" },
  { quote: "Clean Kin is a professional company.", author: "S. Marshall", role: "Verified customer" },
  { quote: "Quality work.",                  author: "W. Turner", role: "Verified customer" },
  { quote: "Highly recommend — again!",      author: "J. Okoro",  role: "Repeat customer" },
];

// Contract Cleans (B2B) — no per-room pricing, quote-based
const B2B_SERVICES = [
  {
    slug: "office-cleaning",
    name: "Office Cleaning",
    description:
      "Productive teams need clean spaces. We keep your office spotless on a contract that fits your schedule.",
    image:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85",
    cta: "Get a FREE Quote",
  },
  {
    slug: "contract-cleaning",
    name: "Contract Cleaning",
    description:
      "Long-term partnerships with dedicated, vetted teams. One point of contact, consistent standards.",
    image:
      "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?crop=entropy&cs=srgb&fm=jpg&q=85",
    cta: "Get a FREE Quote",
  },
  {
    slug: "service-cleaning",
    name: "Service Cleaning",
    description:
      "Retail, hospitality and healthcare environments held to audit-ready standards.",
    image:
      "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?crop=entropy&cs=srgb&fm=jpg&q=85",
    cta: "Get a FREE Quote",
  },
  {
    slug: "specialist-cleaning",
    name: "Specialist Cleaning",
    description:
      "Medical, industrial and post-construction cleans handled by trained specialists.",
    image:
      "https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?crop=entropy&cs=srgb&fm=jpg&q=85",
    cta: "Get a FREE Quote",
  },
];

// Residential Services — fixed pricing, instant booking
const RESIDENTIAL_SERVICES = [
  {
    slug: "end-of-tenancy",
    name: "End of Tenancy",
    description:
      "Worried about your deposit? We hand back inventory-ready properties — landlord approved.",
    image:
      "https://images.unsplash.com/photo-1653242370243-5f7ca54b00db?crop=entropy&cs=srgb&fm=jpg&q=85",
    price: "From £200",
    hours: "4–8 hours",
  },
  {
    slug: "deep-cleaning",
    name: "Deep Cleaning",
    description:
      "The reset your home deserves. Behind appliances, inside cupboards, every detail covered.",
    image:
      "https://images.unsplash.com/photo-1653242370311-8f28f952cc04?crop=entropy&cs=srgb&fm=jpg&q=85",
    price: "From £250",
    hours: "3–6 hours",
  },
  {
    slug: "general-cleaning",
    name: "General Cleaning",
    description:
      "Weekly or fortnightly visits from the same trusted cleaner. Reliable, every time.",
    image:
      "https://images.pexels.com/photos/18435276/pexels-photo-18435276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    price: "From £120",
    hours: "2–4 hours",
  },
  {
    slug: "one-off",
    name: "One-Off Clean",
    description:
      "Hosting guests, post-renovation, or just need a one-time refresh? Book a single session.",
    image:
      "https://images.pexels.com/photos/20285350/pexels-photo-20285350.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    price: "From £160",
    hours: "2–5 hours",
  },
];

const COVERAGE_CITIES = [
  "London",
  "Birmingham",
];

const TRUST_SIGNALS = [
  { icon: Shield, label: "Vetted cleaners" },
  { icon: BadgeCheck, label: "Fully insured" },
  { icon: Lock, label: "Vetted & background-screened" },
  { icon: Sparkles, label: "Quality assured" },
];

const PROBLEMS = [
  {
    pain: "Cleaners who don't show up",
    solution:
      "Every booking is confirmed, tracked and reminded 24 hours before. If a cleaner can't make it, we send a vetted replacement — same standard, same price.",
  },
  {
    pain: "Inconsistent quality between visits",
    solution:
      "We assign you the same dedicated cleaner for recurring bookings. They learn your home, your preferences, and your standards. Consistency, every time.",
  },
  {
    pain: "Unreliable teams that come and go",
    solution:
      "Our cleaners are employed on PAYE contracts and paid above the London Living Wage — not gig-economy strangers. Low turnover means the person you trust today is the same one cleaning next month.",
  },
  {
    pain: "Hidden fees and confusing invoices",
    solution:
      "Transparent, VAT-compliant pricing. You see the full quote before you book. No surprises on the invoice.",
  },
  {
    pain: "Worrying about who's in your home",
    solution:
      "Every cleaner is vetted, ID-verified and fully insured. Trained to a defined Clean Kin standard.",
  },
  {
    pain: "Last-minute cancellations with no warning",
    solution:
      "We confirm 48 and 24 hours ahead — if anything changes on our side, we tell you immediately and put a vetted replacement in place at no extra cost.",
  },
];

const FEATURES = [
  {
    icon: Shield,
    title: "DBS-Checked & Insured",
    description:
      "Every cleaner is background-checked and we carry full public liability insurance — up to £2M cover on every job.",
  },
  {
    icon: Clock,
    title: "Book in Under 60 Seconds",
    description:
      "Postcode, date, done. Online booking with instant confirmation — no callbacks, no chasing.",
  },
  {
    icon: BadgeCheck,
    title: "Trained to a Standard",
    description:
      "Professional cleaners trained on a defined Clean Kin checklist. The cleaner may vary; the standard does not.",
  },
  {
    icon: Sparkles,
    title: "Meticulous With Detail",
    description:
      "Surfaces, edges, high-touch points — checked against a written brief on every visit.",
  },
];

const TESTIMONIALS = [
  {
    quote: "A great clean.",
    author: "Sarah M.",
    location: "London",
    service: "General Cleaning",
    rating: 5,
  },
  {
    quote: "Great attention to detail.",
    author: "A.G.",
    location: "London",
    service: "Deep Cleaning",
    rating: 5,
  },
  {
    quote: "Highly recommend.",
    author: "T.F.",
    location: "Birmingham",
    service: "End of Tenancy",
    rating: 5,
  },
  {
    quote: "Happy with the service.",
    author: "W.T.",
    location: "London",
    service: "Recurring Clean",
    rating: 5,
  },
];

const FAQS = [
  {
    q: "Are your cleaners vetted and insured?",
    a: "Yes — every Clean Kin cleaner is vetted, ID-verified and right-to-work confirmed before joining the team. We carry full public liability insurance up to £2 million on every job.",
  },
  {
    q: "How does pricing work? Are there any hidden fees?",
    a: "Pricing is published per service and per subscription frequency (Weekly, Bi-Weekly, Monthly) based on bedrooms. You see the full quote — including any add-ons and VAT — before you confirm. No call-out fees, no equipment charges. An invoice and receipt are issued automatically once payment is taken.",
  },
  {
    q: "What's your cancellation policy?",
    a: "Cancel or reschedule free of charge up to 24 hours before your booking. Cancellations within 24 hours incur a 50% fee to cover the cleaner's reserved time. Subscriptions can be paused anytime or terminated with 30 days' written notice (see the Subscription Policy on /legal).",
  },
  {
    q: "Do you bring your own equipment and products?",
    a: "Yes — every clean comes with professional-grade products and equipment as standard. If you'd prefer we use your own (allergies, pets, preferences), tick the box at booking.",
  },
  {
    q: "Will the same cleaner come every time?",
    a: "We do not guarantee the same cleaner on every visit. We do guarantee the standard — every cleaner is trained against the same written brief and checklist, and your preferences travel with your booking record.",
  },
  {
    q: "Do you cover my area?",
    a: "We currently cover London and Birmingham. For other locations, email us — we'll let you know when we're expanding.",
  },
];

const REAL_HOMES = [
  {
    src: "https://customer-assets-agu9un31.emergentagent.net/job_eccdbd5d-5806-4fa2-9015-ef51a3d2d2b7/artifacts/9hwpzo9x_WhatsApp%20Image%202026-06-23%20at%2009.46.24.jpeg",
    caption: "Herringbone floors, ready for guests",
    tag: "Deep Clean · West London",
  },
  {
    src: "https://customer-assets-agu9un31.emergentagent.net/job_eccdbd5d-5806-4fa2-9015-ef51a3d2d2b7/artifacts/tl3psqrs_WhatsApp%20Image%202026-06-23%20at%2009.48.39%20%283%29.jpeg",
    caption: "Sunlit garden room, spotless",
    tag: "Weekly Clean · Birmingham",
  },
];

const LandingPage = () => {
  const [postcode, setPostcode] = useState("");
  const [checkingCoverage, setCheckingCoverage] = useState(false);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const checkCoverage = async () => {
    if (!postcode.trim()) {
      toast.error("Please enter a postcode");
      return;
    }
    setCheckingCoverage(true);
    try {
      const response = await axios.post(`${API}/check-coverage`, {
        postcode: postcode.trim(),
      });
      if (response.data.covered) {
        toast.success(
          `Great news! We cover ${response.data.area}. Book your clean today!`
        );
      } else {
        toast.info(
          response.data.message ||
            "We currently serve London and Birmingham. Email us to discuss other locations."
        );
      }
    } catch (error) {
      const prefix = postcode.trim().toUpperCase().replace(/\s/g, "");
      const coveragePrefixes = {
        London: ["E", "EC", "N", "NW", "SE", "SW", "W", "WC"],
        Birmingham: ["B"],
        Bristol: ["BS"],
        Edinburgh: ["EH"],
        Glasgow: ["G"],
        Leeds: ["LS"],
        Liverpool: ["L"],
      };
      let foundCity = null;
      for (const [city, prefixes] of Object.entries(coveragePrefixes)) {
        if (prefixes.some((p) => prefix.startsWith(p))) {
          foundCity = city;
          break;
        }
      }
      if (foundCity) {
        toast.success(`Great news! We cover ${foundCity}.`);
      } else {
        toast.info("We currently serve London and Birmingham. Email us about other locations.");
      }
    } finally {
      setCheckingCoverage(false);
    }
  };

  const handleSubscribe = async () => {
    if (!email.trim() || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubscribing(true);
    try {
      await axios.post(`${API}/subscribe`, { email: email.trim() });
      toast.success("Thank you! We'll keep you updated.");
      setEmail("");
    } catch (error) {
      toast.success("Thank you for your interest! We'll be in touch.");
      setEmail("");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <Layout>
      {/* HERO — problem-solving headline with Ken Burns rotating stills */}
      <section className="relative min-h-[90vh] flex items-center hero-bg leaf-pattern overflow-hidden">
        <div className="absolute inset-0 z-0">
          {HERO_STILLS.map((src, i) => (
            <motion.img
              key={src}
              src={src}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ opacity: 0, scale: 1.15 }}
              animate={{
                opacity: [0, 0.35, 0.35, 0],
                scale: [1.15, 1.0, 1.0, 1.15],
              }}
              transition={{
                duration: HERO_STILLS.length * 6,
                delay: i * 6,
                repeat: Infinity,
                ease: "easeInOut",
                times: [0, 0.05, 0.95, 1],
              }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/40" />
        </div>

        <div className="container-clean relative z-10 py-24">
          <div className="max-w-2xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2D5016] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" strokeWidth={1.5} />
                Vetted • Fully insured • London & Birmingham
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-5xl md:text-7xl font-bold tracking-tight text-[#1A2E12] leading-[1.05] mb-6"
              data-testid="hero-title"
            >
              Spotless homes.<br />
              <span className="text-[#2D5016]">Vetted cleaners.</span><br />
              Zero hassle.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg md:text-xl text-[#5C6B57] leading-relaxed mb-4 max-w-xl"
              data-testid="hero-description"
            >
              Book a vetted, fully insured cleaner online. Professional cleaners trained to a defined Clean Kin standard.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="text-base md:text-lg text-[#2D5016] font-medium mb-8 max-w-xl"
              data-testid="hero-tagline"
            >
              We are passionate about enviro, pet and family-friendly cleaning.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 mb-10"
            >
              <Link to="/booking">
                <Button
                  className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full px-8 py-6 text-lg btn-primary shadow-float"
                  data-testid="hero-cta-book"
                >
                  Get an Instant Quote
                  <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
                </Button>
              </Link>
              <EnquiryDialog source="landing-hero" subject="Talk to a human">
                <Button
                  variant="outline"
                  className="border-[#2D5016] text-[#2D5016] hover:bg-[#E8F5E9] rounded-full px-8 py-6 text-lg"
                  data-testid="hero-cta-email"
                >
                  <Mail className="mr-2 w-5 h-5" strokeWidth={1.5} />
                  Talk to a Human
                </Button>
              </EnquiryDialog>
            </motion.div>

            {/* Trust signal row */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-wrap gap-x-6 gap-y-3"
              data-testid="hero-trust-row"
            >
              {TRUST_SIGNALS.map((t) => (
                <div
                  key={t.label}
                  className="flex items-center gap-2 text-[#1A2E12]"
                >
                  <t.icon
                    className="w-4 h-4 text-[#7CB342]"
                    strokeWidth={2}
                  />
                  <span className="text-sm font-medium">{t.label}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* COVERAGE CHECK */}
      <section className="py-16 bg-[#2D5016] text-white">
        <div className="container-clean">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <MapPin
                className="w-8 h-8 text-[#7CB342]"
                strokeWidth={1.5}
              />
              <div>
                <h3 className="text-xl font-semibold">
                  Check coverage in 5 seconds
                </h3>
                <p className="text-white/70">
                  {COVERAGE_CITIES.join(" • ")}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <Input
                placeholder="Enter your postcode"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 h-12 w-full sm:w-48"
                data-testid="postcode-input"
              />
              <Button
                onClick={checkCoverage}
                disabled={checkingCoverage}
                className="bg-[#7CB342] hover:bg-[#689F38] text-white rounded-full px-6 h-12"
                data-testid="check-coverage-btn"
              >
                {checkingCoverage ? "Checking..." : "Check Coverage"}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM → SOLUTION — condensed grid */}
      <section className="py-14 md:py-20 bg-white" data-testid="problem-solution-section">
        <div className="container-clean">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mb-10"
          >
            <span className="text-sm text-[#7CB342] font-semibold uppercase tracking-widest mb-3 block">
              Why customers switch to Clean Kin
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1A2E12] tracking-tight">
              Cleaning, without the usual headaches.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {PROBLEMS.map((p, i) => (
              <motion.div
                key={p.pain}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="border border-[#E2E8E0] rounded-2xl p-5 bg-[#F9FAF9]"
                data-testid={`problem-card-${i}`}
              >
                <p className="text-[11px] text-[#A23B3B] font-semibold uppercase tracking-wider mb-2">
                  The problem
                </p>
                <p className="text-base font-semibold text-[#1A2E12] mb-3 leading-snug">
                  {p.pain}
                </p>
                <p className="text-[11px] text-[#7CB342] font-semibold uppercase tracking-wider mb-1.5">
                  How we fix it
                </p>
                <p className="text-sm text-[#5C6B57] leading-relaxed">{p.solution}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES — concrete differentiators */}
      <section className="py-24 md:py-32 bg-[#F9FAF9]">
        <div className="container-clean">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm text-[#7CB342] font-semibold uppercase tracking-widest mb-4 block">
              What you get
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold text-[#1A2E12] tracking-tight">
              The Clean Kin standard
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {FEATURES.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="bg-white/80 backdrop-blur-md border-[#E2E8E0] rounded-2xl p-8 h-full hover:shadow-xl hover:shadow-[#2D5016]/5 transition-all duration-500 service-card">
                  <CardContent className="p-0">
                    <div className="w-14 h-14 rounded-2xl bg-[#E8F5E9] flex items-center justify-center mb-6">
                      <feature.icon
                        className="w-7 h-7 text-[#2D5016]"
                        strokeWidth={1.5}
                      />
                    </div>
                    <h3 className="text-xl font-semibold text-[#1A2E12] mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-[#5C6B57] leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CLEAN KIN — trust block */}
      <section className="py-20 md:py-28 bg-[#F9FAF9]" data-testid="why-clean-kin">
        <div className="container-clean">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2D5016] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <BadgeCheck className="w-4 h-4" />
              Why Clean Kin
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold text-[#1A2E12] tracking-tight mb-6">
              Trusted to deliver a service to be proud of.
            </h2>
            <p className="text-lg text-[#5C6B57] leading-relaxed">
              A growing business that is meticulous with detail. Read our reviews, then book with confidence.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { title: "Vetted cleaners", body: "ID-verified, right-to-work confirmed, fully insured." },
              { title: "Defined standard", body: "Written checklist. Trained team. Auditable detail." },
              { title: "Straight talking", body: "Quoted total before payment. Invoice and receipt on every booking." },
            ].map((item) => (
              <div key={item.title} className="bg-white border border-[#E2E8E0] rounded-2xl p-6">
                <h3 className="font-semibold text-[#1A2E12] mb-2">{item.title}</h3>
                <p className="text-sm text-[#5C6B57] leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REAL HOMES — client-supplied photography */}
      <section className="py-20 md:py-24 bg-white" data-testid="real-homes-section">
        <div className="container-clean">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mb-10"
          >
            <span className="text-sm text-[#7CB342] font-semibold uppercase tracking-widest mb-3 block">
              Real Clean Kin homes
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-[#1A2E12] tracking-tight mb-3">
              The finish, straight from the job.
            </h2>
            <p className="text-[#5C6B57] leading-relaxed">
              No stock imagery — these are actual homes our teams handed back this month.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {REAL_HOMES.map((home, i) => (
              <motion.div
                key={home.src}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group relative overflow-hidden rounded-3xl aspect-[4/5] md:aspect-[3/4] shadow-lg"
                data-testid={`real-home-${i}`}
              >
                <img
                  src={home.src}
                  alt={home.caption}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A2E12]/85 via-[#1A2E12]/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                  <span className="inline-block text-[11px] font-semibold uppercase tracking-widest bg-[#7CB342] text-white px-3 py-1 rounded-full mb-3">
                    {home.tag}
                  </span>
                  <p className="text-xl md:text-2xl font-semibold leading-tight">
                    {home.caption}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — motion marquee (fresh client quotes) */}
      <section className="py-14 bg-[#2D5016] text-white overflow-hidden" data-testid="client-quote-marquee">
        <div className="container-clean text-center mb-6">
          <span className="text-xs text-[#B7D89F] font-semibold uppercase tracking-[0.2em]">
            What clients tell us
          </span>
        </div>
        <div className="relative">
          {/* edge fade masks */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-24 z-10 bg-gradient-to-r from-[#2D5016] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-24 z-10 bg-gradient-to-l from-[#2D5016] to-transparent" />
          <motion.div
            className="flex gap-8 whitespace-nowrap"
            animate={{ x: ["0%", "-50%"] }}
            transition={{ duration: 32, ease: "linear", repeat: Infinity }}
          >
            {[...CLIENT_TESTIMONIALS, ...CLIENT_TESTIMONIALS].map((t, i) => (
              <div
                key={`marquee-${i}`}
                className="flex items-center gap-4 shrink-0"
                data-testid={i < CLIENT_TESTIMONIALS.length ? `marquee-quote-${i}` : undefined}
              >
                <Quote className="w-6 h-6 text-[#B7D89F] shrink-0" strokeWidth={1.5} />
                <span className="text-2xl md:text-3xl font-semibold tracking-tight">
                  &ldquo;{t.quote}&rdquo;
                </span>
                <span className="text-sm text-[#B7D89F] ml-2">— {t.author}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIALS — exact quotes from user */}
      <section className="py-24 md:py-32 bg-white" data-testid="testimonials-section">
        <div className="container-clean">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm text-[#7CB342] font-semibold uppercase tracking-widest mb-4 block">
              Real customers, real words
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold text-[#1A2E12] tracking-tight">
              What our clients say
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-[#F9FAF9] border border-[#E2E8E0] rounded-2xl p-8 flex flex-col"
                data-testid={`testimonial-${i}`}
              >
                <Quote
                  className="w-8 h-8 text-[#7CB342] mb-4"
                  strokeWidth={1.5}
                />
                <p className="text-2xl font-semibold text-[#1A2E12] leading-snug mb-6 flex-1">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, idx) => (
                    <Star
                      key={`star-${t.author}-${idx}`}
                      className="w-4 h-4 fill-[#F5B400] text-[#F5B400]"
                    />
                  ))}
                </div>
                <p className="text-sm font-semibold text-[#1A2E12]">
                  {t.author}
                </p>
                <p className="text-sm text-[#5C6B57]">
                  {t.location} · {t.service}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* B2B SERVICES */}
      <section className="py-24 md:py-32 bg-[#F9FAF9]" id="b2b-services">
        <div className="container-clean">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm text-[#7CB342] font-semibold uppercase tracking-widest mb-4 block">
              For Businesses
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold text-[#1A2E12] tracking-tight mb-4">
              Contract cleaning that scales with you
            </h2>
            <p className="text-lg text-[#5C6B57] max-w-2xl mx-auto">
              One supplier. One contact. One consistent standard — across every site you operate.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {B2B_SERVICES.map((service, index) => (
              <motion.div
                key={service.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={`/booking?service=${service.slug}`}
                  data-testid={`service-${service.slug}`}
                >
                  <Card className="group relative overflow-hidden rounded-3xl aspect-[3/4] border-0 service-card cursor-pointer">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A2E12]/90 via-[#1A2E12]/40 to-transparent" />
                    <CardContent className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                      <h3 className="text-2xl font-semibold mb-2">
                        {service.name}
                      </h3>
                      <p className="text-white/80 text-sm leading-relaxed">
                        {service.description}
                      </p>
                      <div className="mt-4 flex items-center text-[#7CB342] font-medium group-hover:translate-x-2 transition-transform">
                        {service.cta}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* RESIDENTIAL SERVICES */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container-clean">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm text-[#7CB342] font-semibold uppercase tracking-widest mb-4 block">
              For Homes
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold text-[#1A2E12] tracking-tight mb-4">
              Pick the clean that fits your week
            </h2>
            <p className="text-lg text-[#5C6B57] max-w-2xl mx-auto">
              From a one-off reset to a weekly visit from the same trusted face.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {RESIDENTIAL_SERVICES.map((service, index) => (
              <motion.div
                key={service.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={`/booking?service=${service.slug}`}
                  data-testid={`service-${service.slug}`}
                >
                  <Card className="group relative overflow-hidden rounded-3xl aspect-[3/4] border-0 service-card cursor-pointer">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A2E12]/90 via-[#1A2E12]/40 to-transparent" />
                    <CardContent className="absolute inset-0 p-6 flex flex-col justify-end text-white">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[#7CB342] font-semibold text-sm">
                          {service.price}
                        </span>
                        <span className="text-white/60 text-xs">·</span>
                        <span className="text-white/70 text-xs">
                          {service.hours}
                        </span>
                      </div>
                      <h3 className="text-2xl font-semibold mb-2">
                        {service.name}
                      </h3>
                      <p className="text-white/80 text-sm leading-relaxed">
                        {service.description}
                      </p>
                      <div className="mt-4 flex items-center text-[#7CB342] font-medium group-hover:translate-x-2 transition-transform">
                        Book Now
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — full coverage */}
      <section
        className="py-24 md:py-32 bg-[#F9FAF9]"
        data-testid="faq-section"
      >
        <div className="container-clean">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1"
            >
              <span className="text-sm text-[#7CB342] font-semibold uppercase tracking-widest mb-4 block">
                FAQs
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold text-[#1A2E12] tracking-tight mb-6">
                Everything you wanted to ask.
              </h2>
              <p className="text-[#5C6B57] leading-relaxed mb-6">
                Insurance, DBS checks, cancellation, pricing, key handling — answered. Still got a question?
              </p>
              <EnquiryDialog source="landing-faq" subject="FAQ follow-up question">
                <button
                  type="button"
                  className="inline-flex items-center gap-2 text-[#2D5016] font-semibold hover:underline"
                  data-testid="faq-email-link"
                >
                  <Mail className="w-4 h-4" />
                  Email Us
                </button>
              </EnquiryDialog>
            </motion.div>

            <div className="lg:col-span-2">
              <Accordion
                type="single"
                collapsible
                className="w-full"
                data-testid="faq-accordion"
              >
                {FAQS.map((f, i) => (
                  <AccordionItem
                    key={f.q}
                    value={`faq-${i}`}
                    className="border-b border-[#E2E8E0]"
                  >
                    <AccordionTrigger
                      className="text-left text-lg font-semibold text-[#1A2E12] hover:no-underline py-6"
                      data-testid={`faq-trigger-${i}`}
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent
                      className="text-[#5C6B57] leading-relaxed pb-6"
                      data-testid={`faq-content-${i}`}
                    >
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* TURNKEY (B2B partners) */}
      <section className="py-24 md:py-32 bg-[#2D5016] text-white texture-grain">
        <div className="container-clean">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-[#7CB342] font-semibold text-sm uppercase tracking-widest mb-4 block">
                For Property Managers & Landlords
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
                One partner for every property in your portfolio.
              </h2>
              <p className="text-white/80 text-lg leading-relaxed mb-8">
                End-of-tenancy turnarounds, scheduled deep cleans, supplier-managed stock and laundry — all under one account, one invoice, one point of contact.
              </p>

              <ul className="space-y-4 mb-8">
                {[
                  "Vetted supplier network with audited standards",
                  "Automated stock replenishment between visits",
                  "Professional laundry & linen turnaround",
                  "Dedicated account manager — you call one number",
                ].map((item) => (
                  <li
                    key={`feature-${item}`}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle2
                      className="w-5 h-5 text-[#7CB342]"
                      strokeWidth={1.5}
                    />
                    <span className="text-white/90">{item}</span>
                  </li>
                ))}
              </ul>

              <Link to="/partners">
                <Button
                  className="bg-white text-[#2D5016] hover:bg-[#E8F5E9] rounded-full px-8 py-6 text-lg btn-primary"
                  data-testid="turnkey-cta"
                >
                  Talk to a Partnerships Lead
                  <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <img
                src="https://images.pexels.com/photos/8709568/pexels-photo-8709568.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Professional cleaning supplies"
                className="rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                    <Wind
                      className="w-6 h-6 text-[#2D5016]"
                      strokeWidth={1.5}
                    />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A2E12]">
                      Audit-ready
                    </p>
                    <p className="text-sm text-[#5C6B57]">
                      VAT-compliant invoicing
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container-clean">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-[#E8F5E9] rounded-3xl p-12 md:p-16 text-center"
          >
            <h2 className="text-4xl md:text-5xl font-semibold text-[#1A2E12] tracking-tight mb-6">
              Stop wasting weekends on cleaning.
            </h2>
            <p className="text-lg text-[#5C6B57] max-w-2xl mx-auto mb-8">
              Get an instant quote in under a minute. Same-week availability across our coverage areas.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/booking">
                <Button
                  className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full px-8 py-6 text-lg btn-primary"
                  data-testid="cta-book"
                >
                  Get an Instant Quote
                  <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
                </Button>
              </Link>
              <EnquiryDialog source="landing-cta" subject="General enquiry">
                <Button
                  variant="outline"
                  className="border-[#2D5016] text-[#2D5016] hover:bg-white rounded-full px-8 py-6 text-lg"
                  data-testid="cta-email"
                >
                  <Mail className="mr-2 w-5 h-5" strokeWidth={1.5} />
                  Email Us
                </Button>
              </EnquiryDialog>
            </div>

            <div className="border-t border-[#2D5016]/10 pt-8">
              <p className="text-[#5C6B57] mb-4">
                Cleaning tips, offers and seasonal reminders — straight to your inbox.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 bg-white"
                  data-testid="newsletter-email"
                />
                <Button
                  onClick={handleSubscribe}
                  disabled={subscribing}
                  className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full px-6 h-12"
                  data-testid="newsletter-submit"
                >
                  {subscribing ? "..." : "Subscribe"}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default LandingPage;
